'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Box, Typography } from '@mui/material'
import { LinkData } from '@models'
import { PaginationSection } from '@components/common'
import { BlogsCard, BlogsCardData } from '@components/common/Cards/BlogsCard'
import { getStyles } from '@utils/styles'
import defaultStyle from './styles'

export type BlogsSectionData = {
  heading: string
  category: LinkData[]
  totalCards: number
  cards: BlogsCardData[]
  seasonalCards?: BlogsCardData[]
}

const diagnosticLinks = [
  ['Complete Blood Count (CBC)', '/tests/complete-blood-count-cbc'],
  ['Thyroid Profile', '/tests/thyroid-function-test-tft'],
  ['Liver Function Test', '/tests/liver-function-lft'],
  ['Kidney Function Test', '/tests/kidney-function-kft'],
  ['Vitamin D Test', '/tests/vitamin-d-25-hydroxy'],
]

const blogCategories = [
  {
    label: 'Diseases & Conditions',
    links: [
      ['Diabetes', '/blogs/category/diabetes'],
      ['Thyroid', '/blogs/category/thyroid'],
      ['Heart Diseases', '/blogs/category/heart-diseases'],
      ['Kidney', '/blogs/category/kidney'],
    ],
  },
  {
    label: 'Tests & Diagnostics',
    links: [
      ['Diagnostics & Pathology', '/blogs/category/diagnostics-pathology'],
      ['Medical Tests', '/blogs/category/medical-tests'],
      ['Health Checkups', '/blogs/category/health-checkups'],
    ],
  },
  {
    label: 'Preventive Health',
    links: [
      ['Health Tips', '/blogs/category/health-tips'],
      ['Fitness', '/blogs/category/fitness'],
      ['Health Checkups', '/blogs/category/health-checkups'],
    ],
  },
  {
    label: 'Nutrition & Diet',
    links: [
      ['Nutrition & Recipes', '/blogs/category/nutrition-recipes'],
      ['Health Supplements', '/blogs/category/health-supplements'],
      ['Food Intolerance', '/blogs/category/food-intolerance'],
    ],
  },
  {
    label: "Women's Health",
    links: [
      ["Women's Health", '/blogs/category/womens-health'],
      ['Pregnancy', '/blogs/category/pregnancy'],
      ['Menstrual Problems', '/blogs/category/menstrual-problems'],
    ],
  },
  {
    label: "Men's Health",
    links: [["Men's Health", '/blogs/category/mens-health']],
  },
  {
    label: 'Child & Elderly Care',
    links: [
      ['Child Health', '/blogs/category/child-health'],
      ['Parent Care & Old Age', '/blogs/category/parent-care-old-age'],
    ],
  },
  {
    label: 'Skin & Allergies',
    links: [
      ['Skin & Hair Problems', '/blogs/category/skin-hair-problems'],
      ['Allergy', '/blogs/category/allergy'],
    ],
  },
  {
    label: 'Weight Management',
    links: [['Weight Management', '/blogs/category/weight-management']],
  },
]

const cityLinks = [
  ['Mumbai', '/find-nearest-centre/mumbai'],
  ['Delhi', '/find-nearest-centre/delhi'],
  ['Bengaluru', '/find-nearest-centre/bangalore'],
  ['Hyderabad', '/find-nearest-centre/hyderabad'],
  ['Chennai', '/find-nearest-centre/chennai'],
  ['Kolkata', '/find-nearest-centre/kolkata'],
]

// eslint-disable-next-line complexity
export function BlogsSection({
  heading,
  totalCards,
  cards,
  seasonalCards = [],
}: BlogsSectionData) {
  const seasonalSliderRef = useRef<HTMLDivElement>(null)
  const [categoriesOpen, setCategoriesOpen] = useState(true)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const styles = getStyles(defaultStyle)
  const searchParams = useSearchParams()
  const selectedView = searchParams.get('view')
  const showAllLatest = selectedView === 'latest'
  const showAllSeasonal = selectedView === 'seasonal-trending'
  const scrollSeasonal = (direction: -1 | 1) => {
    seasonalSliderRef.current?.scrollBy({
      left: direction * 320,
      behavior: 'smooth',
    })
  }
  const filteredCards = cards

  return (
    <Box {...styles('page')}>
      <Box {...styles('contentGrid')}>
        <Box>
          <Box {...styles('sectionHeader')}>
            <Box>
              <Typography {...styles('sectionBadge')}>LATEST BLOGS</Typography>
              <Typography component="h1" {...styles('pageTitle')}>
                {heading === 'Blogs' ? 'Latest Blogs' : heading}
              </Typography>
              <Typography {...styles('subtitle')}>
                Fresh perspectives on health, diagnostics and wellness
              </Typography>
            </Box>
            <Link
              href={showAllLatest ? '/blogs' : '/blogs?view=latest'}
              style={{ textDecoration: 'none' }}
            >
              <Typography {...styles('seeAll')}>
                {showAllLatest ? 'Show Less ↑' : 'See All →'}
              </Typography>
            </Link>
          </Box>
          <Box {...styles('cardGrid')}>
            {filteredCards
              .slice(0, showAllLatest ? filteredCards.length : 4)
              .map((item) => (
                <BlogsCard
                  {...item}
                  key={`${item.heading}-${item.buttonProps.link}`}
                  customStyles={{
                    card: styles('blogCard').sx,
                    image: styles('blogImage').sx,
                    heading: styles('blogHeading').sx,
                    description: styles('blogDescription').sx,
                  }}
                />
              ))}
          </Box>
          <Box id="seasonal-trending" {...styles('seasonalSection')}>
            <Box {...styles('sectionHeader')}>
              <Box>
                <Typography {...styles('sectionBadge')}>
                  SEASONAL TRENDING BLOGS
                </Typography>
                <Typography component="h2" {...styles('seasonalTitle')}>
                  Seasonal Trending Blogs
                </Typography>
                <Typography {...styles('subtitle')}>
                  Handpicked seasonal health reads and expert medical insights
                </Typography>
              </Box>
              <Link
                href={
                  showAllSeasonal
                    ? '/blogs#seasonal-trending'
                    : '/blogs?view=seasonal-trending#seasonal-trending'
                }
                style={{ textDecoration: 'none' }}
              >
                <Typography {...styles('seeAll')}>
                  {showAllSeasonal ? 'Show Less ↑' : 'See All →'}
                </Typography>
              </Link>
            </Box>
            {seasonalCards.length > 0 && (
              <Box {...styles('sliderWrap')}>
                <Box
                  component="button"
                  type="button"
                  aria-label="Previous seasonal blogs"
                  onClick={() => scrollSeasonal(-1)}
                  {...styles(['sliderButton', 'sliderButtonPrevious'])}
                >
                  ‹
                </Box>
                <Box ref={seasonalSliderRef} {...styles('seasonalSlider')}>
                  {seasonalCards
                    .slice(0, showAllSeasonal ? seasonalCards.length : 8)
                    .map((item) => (
                      <Box
                        key={item.buttonProps.link}
                        {...styles('seasonalSlide')}
                      >
                        <BlogsCard
                          {...item}
                          customStyles={{
                            card: styles('seasonalCard').sx,
                            image: styles('seasonalImage').sx,
                            heading: styles('seasonalHeading').sx,
                            description: styles('seasonalDescription').sx,
                          }}
                        />
                      </Box>
                    ))}
                </Box>
                <Box
                  component="button"
                  type="button"
                  aria-label="Next seasonal blogs"
                  onClick={() => scrollSeasonal(1)}
                  {...styles(['sliderButton', 'sliderButtonNext'])}
                >
                  ›
                </Box>
              </Box>
            )}
          </Box>
          <PaginationSection totalItems={totalCards} currentPage={1} />
        </Box>

        <Box component="aside" {...styles('sidebar')}>
          <Box {...styles('categoryPanel')}>
            <Box
              component="button"
              type="button"
              aria-expanded={categoriesOpen}
              aria-controls="blog-listing-category-links"
              onClick={() => setCategoriesOpen((open) => !open)}
              {...styles('categoryHeader')}
            >
              <Typography {...styles('categoryHeaderTitle')}>
                Blog Categories
              </Typography>
              <Box {...styles('categoryHeaderMeta')}>
                <Typography {...styles('categoryCount')}>
                  9 Categories
                </Typography>
                <Box
                  component="span"
                  {...styles(
                    categoriesOpen
                      ? ['categoryChevron', 'categoryChevronOpen']
                      : 'categoryChevron'
                  )}
                >
                  ⌄
                </Box>
              </Box>
            </Box>
            {categoriesOpen && (
              <Box
                id="blog-listing-category-links"
                {...styles('categoryLinks')}
              >
                {blogCategories.map(({ label, links }) => {
                  const isOpen = expandedCategory === label
                  return (
                    <Box key={label} {...styles('categoryItem')}>
                      <Box
                        component="button"
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setExpandedCategory(isOpen ? null : label)
                        }
                        {...styles('categoryLink')}
                      >
                        {label}
                        <Box
                          component="span"
                          {...styles(
                            isOpen
                              ? ['itemChevron', 'itemChevronOpen']
                              : 'itemChevron'
                          )}
                        >
                          ›
                        </Box>
                      </Box>
                      {isOpen && (
                        <Box {...styles('categorySubLinks')}>
                          {links.map(([subLabel, href]) => (
                            <Link
                              href={href}
                              key={subLabel}
                              style={{ textDecoration: 'none' }}
                            >
                              <Typography {...styles('categorySubLink')}>
                                {subLabel}
                              </Typography>
                            </Link>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box {...styles('cta')}>
        <Box>
          <Typography {...styles('ctaLabel')}>NEED ASSISTANCE?</Typography>
          <Typography component="h2" {...styles('ctaTitle')}>
            Book a Test or Get Expert Health Guidance
          </Typography>
          <Typography {...styles('ctaText')}>
            Our health advisors can help you choose a test or health package.
          </Typography>
        </Box>
        <Box {...styles('ctaActions')}>
          <Link href="/book-a-test" style={{ textDecoration: 'none' }}>
            <Box component="span" {...styles('primaryAction')}>
              Book Blood Test Now
            </Box>
          </Link>
          <Box
            component="button"
            type="button"
            onClick={() =>
              window.dispatchEvent(new Event('apollo:open-callback-form'))
            }
            {...styles('secondaryAction')}
          >
            Request Quick Callback
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography {...styles('quickTitle')}>
          Top Diagnostic Tests, Packages &amp; Centre Locations
        </Typography>
        <Box {...styles('quickGrid')}>
          <Box {...styles('quickPanel')}>
            <Typography {...styles('quickHeading')}>Top Tests</Typography>
            <Box {...styles('linkList')}>
              {diagnosticLinks.map(([label, href]) => (
                <Link
                  href={href}
                  key={label}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography {...styles('quickLink')}>{label}</Typography>
                </Link>
              ))}
            </Box>
          </Box>
          <Box {...styles('quickPanel')}>
            <Typography {...styles('quickHeading')}>Health Packages</Typography>
            <Box {...styles('linkList')}>
              <Link
                href="/health-check-packages"
                style={{ textDecoration: 'none' }}
              >
                <Typography {...styles('quickLink')}>
                  Full Body Checkups
                </Typography>
              </Link>
              <Link href="/mens-health" style={{ textDecoration: 'none' }}>
                <Typography {...styles('quickLink')}>
                  Men&apos;s Health Packages
                </Typography>
              </Link>
              <Link href="/womens-health" style={{ textDecoration: 'none' }}>
                <Typography {...styles('quickLink')}>
                  Women&apos;s Health Packages
                </Typography>
              </Link>
            </Box>
          </Box>
          <Box {...styles('quickPanel')}>
            <Typography {...styles('quickHeading')}>
              Diagnostic Centres
            </Typography>
            <Box {...styles('linkList')}>
              {cityLinks.map(([label, href]) => (
                <Link
                  href={href}
                  key={label}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography {...styles('quickLink')}>
                    Centres in {label}
                  </Typography>
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
